import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import api from '../../services/api';
import { C, AC_COLOURS, isNum, pct, num, money, V, Dash, signCls, round10, useFetch, Status, Tile } from './helpers';
import { OrderForm } from './PlusFundOrders';

const pctTick = (v) => (v * 100).toFixed(0) + '%';
const pctTip = (v) => (isNum(v) ? (v * 100).toFixed(2) + '%' : '–');
const mTick = (v) => 'A$' + (v / 1e6).toFixed(0) + 'm';
const GROUP_COLOURS = { 'Aus Equities': C.navy, 'Global Equities': C.teal, Alternatives: C.tealDark, Credit: C.slate,
  Bonds: C.sage, 'Portfolio Protection': C.mauve, FX: C.lightBlue, 'Cash & interest': C.greyMid };

// ---- Front page additions ---------------------------------------------------
export function MtdTiles({ valuation }) {
  const v = valuation;
  if (!v) return null;
  const m = v.market;
  return (
    <>
      <Tile label="Fund MTD estimate" value={pct(v.fund_mtd_estimate, 2, true)}
            sub={isNum(v.fund_mtd_nav_part) ? `NAV ${pct(v.fund_mtd_nav_part, 2, true)} to ${m.fund_nav.date} + prices ${pct(v.fund_mtd_price_part, 2, true)}` : 'fund NAV series not loaded'} />
      <Tile label="AUD/USD MTD" value={pct(m.audusd.mtd, 2, true)} sub={isNum(m.audusd.rate) ? `${m.audusd.rate.toFixed(4)} at ${m.audusd.date}` : 'AUD= series not loaded'} />
    </>
  );
}

export function CurrencyStrip({ data }) {
  if (!data) return null;
  return (
    <div className="pf-toolbar" title="Physical FX exposure (by FX_EXP) plus cash, by currency, as a share of NAV">
      <span className="pf-muted">Currency exposure:</span>
      {data.rows.filter((r) => Math.abs(r.total) > 0.0005).map((r) => (
        <span key={r.currency} className="pf-pill pf-pill-navy">{r.currency} {pct(r.total, 1)} <span className="pf-sub">(phys {pct(r.physical, 1)} · cash {pct(r.cash, 1)})</span></span>
      ))}
      <span className="pf-pill pf-pill-navy">Non-AUD {pct(data.non_aud, 1)}</span>
    </div>
  );
}

// ---- Valuation tab ------------------------------------------------------------
export function ValuationTab() {
  const saved = useFetch('/api/plus-fund/inputs/valuation');
  const [apexDate, setApexDate] = useState('');
  const [flows, setFlows] = useState([]);
  const [margin, setMargin] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const val = useFetch('/api/plus-fund/valuation', apexDate ? { apex_date: apexDate } : null);
  useEffect(() => {
    const v = saved.data?.value;
    if (v && !dirty) { setApexDate(v.apex_date || ''); setFlows(v.flows || []); setMargin(v.margin || {}); }
  }, [saved.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // The valuation endpoint auto-clears flows IB has booked as cash and saves the shorter
  // list; drop exactly those rows here too (other unsaved edits kept) so a later save
  // cannot put them back.
  useEffect(() => {
    const cl = val.data?.cleared_flows || [];
    if (!cl.length) return;
    const same = (a, b) => (a.date || '') === (b.date || '') && Number(a.amount) === Number(b.amount) && (a.label || '') === (b.label || '');
    setFlows((fs) => {
      const left = [...cl];
      return fs.filter((f) => { const i = left.findIndex((c) => same(c, f)); if (i < 0) return true; left.splice(i, 1); return false; });
    });
    if (!dirty) saved.reload();
  }, [val.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await api.put('/api/plus-fund/inputs/valuation', { value: { apex_date: apexDate, flows, margin } });
      setDirty(false); val.reload(); saved.reload();
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setSaving(false); }
  };
  const v = val.data;
  const flowsTotal = flows.reduce((a, f) => a + (Number(f.amount) || 0), 0);
  return (
    <>
      <div className="pf-note">
        Replaces the workbook's Holdings sheet. Every holding is revalued at the APEX valuation date (close x FX x quantity, Datastream) and
        at the latest close; cash carries one day's accrual at the 1-month rate (plus the IB margin on short balances). Pending applications
        and redemptions not yet in IB cash are added to give the APEX NAV estimate. Fund MTD estimate = Datastream fund NAV month to date
        plus the weighted price moves since that NAV date (Top!T4).
      </div>
      <div className="pf-add-row">
        <label>APEX valuation date<input type="date" value={apexDate} onChange={(e) => { setApexDate(e.target.value); setDirty(true); }} /></label>
        {['AUD', 'USD', 'EUR', 'GBP', 'JPY'].map((c) => (
          <label key={c}>IB margin {c} (%)<input type="number" step="0.05" value={margin[c] ?? (v?.margin?.[c] ?? '')}
            onChange={(e) => { setMargin({ ...margin, [c]: e.target.value === '' ? undefined : Number(e.target.value) }); setDirty(true); }} /></label>
        ))}
        <button className="pf-btn" onClick={save} disabled={saving || !dirty}>Save inputs</button>
        {saved.data?.updated_by && <span className="pf-sub">saved by {saved.data.updated_by}</span>}
      </div>
      <div className="pf-section"><h2>Pending flows (not yet in IB cash)</h2></div>
      <div className="pf-muted">A flow clears itself once IB books the matching cash (a deposit or withdrawal of the same A$ amount in the fund account, dated no more than 5 days before the flow's date), because it is then already in the IB cash. Give each flow a date so it can clear.</div>
      {v?.cleared_flows?.length > 0 && (
        <div className="pf-note">Cleared, now in IB cash: {v.cleared_flows.map((f) => `${f.label ? `${f.label} ` : ''}A$${num(f.amount)} (${f.ib_type === 'DEP' ? 'deposit' : 'withdrawal'} booked ${f.ib_date})`).join(' · ')}</div>
      )}
      <div className="pf-table-scroll" style={{ maxWidth: 720 }}>
        <table className="pf-table pf-apl-table">
          <thead><tr><th className="pf-l">Label</th><th className="pf-l">Date</th><th>Amount A$ (redemptions negative)</th><th /></tr></thead>
          <tbody>
            {flows.map((f, i) => (
              <tr key={i}>
                <td><input value={f.label || ''} onChange={(e) => { const n = [...flows]; n[i] = { ...f, label: e.target.value }; setFlows(n); setDirty(true); }} /></td>
                <td><input type="date" value={f.date || ''} onChange={(e) => { const n = [...flows]; n[i] = { ...f, date: e.target.value }; setFlows(n); setDirty(true); }} /></td>
                <td><input className="pf-num" style={{ width: 130 }} type="number" value={f.amount ?? ''} onChange={(e) => { const n = [...flows]; n[i] = { ...f, amount: e.target.value === '' ? null : Number(e.target.value) }; setFlows(n); setDirty(true); }} /></td>
                <td><button className="pf-link" onClick={() => { setFlows(flows.filter((_, j) => j !== i)); setDirty(true); }}>remove</button></td>
              </tr>
            ))}
            <tr className="pf-total"><td className="pf-l" colSpan={2}>Total pending</td><td className="pf-r">{num(flowsTotal)}</td>
              <td><button className="pf-link" onClick={() => { setFlows([...flows, { label: '', date: '', amount: null }]); setDirty(true); }}>add</button></td></tr>
          </tbody>
        </table>
      </div>
      {err && <div className="pf-alert">{String(err)}</div>}
      <Status loading={val.loading} error={val.error} />
      {v && (
        <>
          {v.missing_price.length > 0 && <div className="pf-alert">No Datastream price for: {v.missing_price.join(', ')}. These are excluded from the live and APEX values until the series load (06:23 refresh).</div>}
          <div className="pf-tiles">
            <Tile label={`IB NAV ${v.ib_date}`} value={money(v.ib_nav)} />
            <Tile label="Live NAV" value={money(v.live_nav)} sub="latest closes + live cash" />
            <Tile label={`APEX NAV est. ${v.apex_date}`} value={money(v.apex_nav_plus_flows)} sub={`${money(v.apex_nav)} + flows ${money(v.flows_total)}`} />
            <Tile label="APEX % change vs IB" value={pct(v.apex_pct_change, 2, true)} />
            <Tile label="Fund MTD estimate" value={pct(v.fund_mtd_estimate, 2, true)} sub={isNum(v.fund_mtd_nav_part) ? `NAV ${pct(v.fund_mtd_nav_part, 2, true)} + prices ${pct(v.fund_mtd_price_part, 2, true)}` : 'fund NAV series not loaded'} />
            <Tile label="AUD/USD MTD" value={pct(v.market.audusd.mtd, 2, true)} />
          </div>
          <div className="pf-section"><h2>Holdings</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Symbol</th><th className="pf-l">Ccy</th><th>Quantity</th><th>IB value</th><th>Price (live)</th><th className="pf-l">Date</th><th>FX live</th><th>A$ live</th><th>Price APEX</th><th>FX APEX</th><th>A$ APEX</th><th>Px at NAV date</th><th>Since NAV date</th></tr></thead>
              <tbody>
                {v.holdings.map((h) => (
                  <tr key={h.security_id}>
                    <td className="pf-l"><strong>{h.symbol}</strong></td><td className="pf-l">{h.currency}</td>
                    <td className="pf-r"><V v={h.quantity} kind="num" /></td><td className="pf-r"><V v={h.ib_value} kind="num" /></td>
                    <td className="pf-r"><V v={h.price_live} kind="num" dp={2} /></td><td className="pf-l pf-sub">{h.price_date || ''}</td>
                    <td className="pf-r"><V v={h.fx_live} kind="num" dp={4} /></td><td className="pf-r"><V v={h.value_live} kind="num" /></td>
                    <td className="pf-r"><V v={h.price_apex} kind="num" dp={2} /></td><td className="pf-r"><V v={h.fx_apex} kind="num" dp={4} /></td>
                    <td className="pf-r"><V v={h.value_apex} kind="num" /></td><td className="pf-r"><V v={h.price_at_nav_date} kind="num" dp={2} /></td>
                    <td className={`pf-r ${signCls(h.change_since_nav_date)}`}><V v={h.change_since_nav_date} dp={2} signed /></td>
                  </tr>
                ))}
                <tr className="pf-total"><td className="pf-l" colSpan={7}>Options / futures (IB value)</td><td className="pf-r"><V v={v.other_value} kind="num" /></td><td colSpan={5} /></tr>
              </tbody>
            </table>
          </div>
          <div className="pf-section"><h2>Cash and accrual</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Ccy</th><th>Balance (local)</th><th>IB value A$</th><th>1m rate %</th><th>IB margin %</th><th>Daily long</th><th>Daily short</th><th>FX live</th><th>Live cash A$</th><th>FX APEX</th><th>T+1 cash (APEX)</th></tr></thead>
              <tbody>
                {v.cash.map((c) => (
                  <tr key={c.currency}>
                    <td className="pf-l"><strong>{c.currency}</strong></td><td className="pf-r"><V v={c.quantity} kind="num" /></td><td className="pf-r"><V v={c.ib_value} kind="num" /></td>
                    <td className="pf-r">{isNum(c.rate_1m) ? c.rate_1m.toFixed(4) : <Dash />}</td><td className="pf-r">{isNum(c.ib_margin) ? c.ib_margin.toFixed(2) : <Dash />}</td>
                    <td className="pf-r">{isNum(c.daily_long) ? (c.daily_long * 100).toFixed(5) + '%' : <Dash />}</td><td className="pf-r">{isNum(c.daily_short) ? (c.daily_short * 100).toFixed(5) + '%' : <Dash />}</td>
                    <td className="pf-r"><V v={c.fx_live} kind="num" dp={4} /></td><td className="pf-r"><V v={c.live_cash} kind="num" /></td>
                    <td className="pf-r"><V v={c.fx_apex} kind="num" dp={4} /></td><td className="pf-r"><V v={c.t1_cash_apex} kind="num" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pf-foot">Rates: {Object.entries(v.market.rates_1m).map(([c, r]) => `${c} ${r.series}${isNum(r.rate) ? ' ' + r.rate.toFixed(3) + '%' : ' (not loaded)'}`).join(' · ')}. FX: {Object.entries(v.market.fx_to_aud).filter(([c]) => c !== 'AUD').map(([c, r]) => `${c} ${r.series}${isNum(r.rate) ? ' ' + r.rate.toFixed(4) : ' (not loaded)'}`).join(' · ')}. Datastream as at {v.market.as_at}.</div>
        </>
      )}
    </>
  );
}

// ---- Trade ticket ---------------------------------------------------------------
const EMPTY_ROW = { ticker: '', name: '', asset_type: 'STK', gd: 'Growth', trade_pct: 0, ccy: 'AUD', multiplier: 1, delta: 1, price: null, fx: null, ds_ticker: '' };

export function TicketTab({ summary, valuation, apl }) {
  const saved = useFetch('/api/plus-fund/inputs/ticket');
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState(null);
  const [fetching, setFetching] = useState(false);
  useEffect(() => { const v = saved.data?.value; if (v?.rows && !dirty) setRows(v.rows); }, [saved.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const nav = valuation?.sheet_live_nav || valuation?.live_nav || summary?.stats?.NAV_Fund;
  const snAdj = summary?.stats?.SN_Adjustment;
  const stats = summary?.stats || {};
  const aplBySym = useMemo(() => { const m = {}; (apl?.rows || []).forEach((r) => { if (r.symbol) m[r.symbol] = r; }); return m; }, [apl]);

  const update = (i, patch) => { const n = [...rows]; n[i] = { ...n[i], ...patch }; setRows(n); setDirty(true); };
  const fetchPrices = async () => {
    setFetching(true); setErr(null);
    try {
      const ds = rows.map((r) => r.ds_ticker || aplBySym[r.ticker]?.ds_ticker).filter(Boolean);
      const r = await api.post('/api/plus-fund/prices', { ds_tickers: ds });
      setRows(rows.map((row) => {
        const d = row.ds_ticker || aplBySym[row.ticker]?.ds_ticker;
        const p = d ? r.data.prices[d] : null;
        const fx = row.ccy === 'AUD' ? 1 : r.data.fx_to_aud[row.ccy];
        return { ...row, ds_ticker: d || row.ds_ticker, price: p?.price ?? row.price, price_date: p?.date, fx: fx ?? row.fx, price_reason: p?.reason };
      }));
      setDirty(true);
    } catch (e) { setErr(e.response?.data?.detail || e.message); } finally { setFetching(false); }
  };
  // Autosave: every edit (including remove / clear) is written ~1s after the last change,
  // and any pending change is flushed when the tab unmounts, so the shared ticket in the
  // RDS always matches the screen (it used to save only on the button, so deleted rows
  // came back when the tab reloaded). `ver` guards an edit made while a save is in flight.
  const ver = useRef(0);
  const latest = useRef({ rows, dirty });
  latest.current = { rows, dirty };
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const v = ver.current;
    setErr(null); setSaving(true);
    try {
      await api.put('/api/plus-fund/inputs/ticket', { value: { rows } });
      if (ver.current === v) { setDirty(false); saved.reload(); }
    } catch (e) { setErr(`Ticket not saved: ${e.response?.data?.detail || e.message}`); } finally { setSaving(false); }
  };
  useEffect(() => { if (dirty) ver.current += 1; }, [rows, dirty]);
  useEffect(() => {
    if (!dirty) return undefined;
    const id = setTimeout(save, 900);
    return () => clearTimeout(id);
  }, [rows, dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => {
    const { rows: r, dirty: d } = latest.current;
    if (d) api.put('/api/plus-fund/inputs/ticket', { value: { rows: r } }).catch(() => {});
  }, []);
  // Top!P47:W51 maths
  const calc = rows.map((r) => {
    const price = r.price === null || r.price === undefined || r.price === '' ? NaN : Number(r.price);
    const fxRaw = r.ccy === 'AUD' ? 1 : (r.fx === null || r.fx === undefined || r.fx === '' ? NaN : Number(r.fx));
    const fx = fxRaw, mult = Number(r.multiplier) || 1, delta = Number(r.delta) || 1;
    const tp = (Number(r.trade_pct) || 0) / 100;          // entered in percent (5 = 5% of NAV)
    if (!isNum(nav) || !isNum(price) || !isNum(fx) || price === 0 || fx === 0) return {};
    const qty = r.asset_type === 'OPT' ? round10(tp * nav / (price * delta * fx * mult)) : round10(tp * nav / (price * fx * mult));
    const locVal = price * qty * fx;
    const notional = r.asset_type === 'OPT' ? (price * qty * fx * delta * mult) / nav : r.asset_type === 'FUT' ? (fx * qty * mult * price) / nav : (fx * price * qty) / nav;
    return { qty, qty_sn: isNum(snAdj) ? round10(qty * snAdj) : null, loc_val: locVal, phys_weight: locVal / nav, aud_val: qty * price * fx, notional };
  });
  // order lines from the sized ticket rows: unsigned quantities, side from the sign
  const [orderLines, setOrderLines] = useState(null);
  const [logged, setLogged] = useState(null);
  const openOrder = () => {
    setLogged(null);
    setOrderLines(rows.map((r, i) => ({ r, c: calc[i] })).filter(({ c }) => isNum(c.qty) && c.qty !== 0).map(({ r, c }) => {
      const a = aplBySym[r.ticker];
      return {
        line_type: 'security', side: c.qty > 0 ? 'BUY' : 'SELL', security_id: a?.security_id || null, symbol: r.ticker,
        description: r.name, asset_type: r.asset_type, currency: r.ccy, multiplier: Number(r.multiplier) || 1, delta: Number(r.delta) || 1,
        price: Number(r.price), price_date: r.price_date || null, fx_rate: r.ccy === 'AUD' ? 1 : Number(r.fx), trade_pct: r.trade_pct,
        qty_fund: Math.abs(c.qty), qty_sn: isNum(c.qty_sn) ? Math.abs(c.qty_sn) : null,
      };
    }));
  };
  const sumN = calc.reduce((a, c) => a + (c.notional || 0), 0);
  const sumG = calc.reduce((a, c, i) => a + (rows[i].gd === 'Growth' ? (c.notional || 0) : 0), 0);
  const sumD = calc.reduce((a, c, i) => a + (rows[i].gd === 'Defensive' ? (c.notional || 0) : 0), 0);
  const checks = [
    { label: 'Gross', pre: stats.Fund_Gross, post: isNum(stats.Fund_Gross) ? stats.Fund_Gross + sumN : null, limit: 3 },
    { label: 'Defensive gross', pre: stats.Defensive_Gross, post: isNum(stats.Defensive_Gross) ? stats.Defensive_Gross + sumD : null, limit: 3 },
    { label: 'Growth gross', pre: stats.Growth_Gross, post: isNum(stats.Growth_Gross) ? stats.Growth_Gross + sumG : null, limit: 1.5 },
  ];
  return (
    <>
      <div className="pf-note">Replaces Top!TRADING. Enter candidate trades as a percent of NAV (5 = buy 5%, -3 = sell 3%); quantities are rounded to 10 units (Top!V47), SN quantity = fund quantity x SN adjustment. A row stays blank until it has a price and (for non-AUD) an FX rate, both of which can be typed in when the Datastream series is not loaded. Prices and FX come from Datastream's latest close (fetch), and can be overtyped. NAV used: {money(nav) || '–'} ({valuation ? 'live' : 'IB'}). The ticket is shared and autosaves to the RDS as you edit.</div>
      <div className="pf-toolbar">
        <button className="pf-btn pf-btn-light" onClick={() => { setRows([...rows, { ...EMPTY_ROW }]); setDirty(true); }}>Add row</button>
        <button className="pf-btn pf-btn-light" onClick={fetchPrices} disabled={fetching || rows.length === 0}>Fetch prices and FX</button>
        <span className={dirty || saving ? "pf-muted" : "pf-saved"}>{saving ? "Saving…" : dirty ? "Unsaved changes…" : "All changes saved"}</span>
        <button className="pf-link" onClick={() => { setRows([]); setDirty(true); }}>clear</button>
        <button className="pf-btn" onClick={openOrder} disabled={!!orderLines}>Log order from ticket…</button>
        {saved.data?.updated_by && <span className="pf-sub">saved by {saved.data.updated_by} {saved.data.updated_at?.slice(0, 16).replace('T', ' ')}</span>}
      </div>
      {err && <div className="pf-alert">{String(err)}</div>}
      {logged && <div className="pf-note">Order <strong>{logged.order_ref}</strong> logged and awaiting approval by a second person: see the Trade log tab.</div>}
      {orderLines && (
        <OrderForm initialLines={orderLines} nav={nav} snAdj={snAdj} aplBySym={aplBySym}
          fxToAud={Object.fromEntries(Object.entries(valuation?.market?.fx_to_aud || {}).map(([c, r]) => [c, r?.rate]))}
          onDone={(o) => { setLogged(o); setOrderLines(null); }} onClose={() => setOrderLines(null)} />
      )}
      <div className="pf-table-scroll">
        <table className="pf-table pf-apl-table">
          <thead><tr><th className="pf-l">Ticker</th><th className="pf-l">Name</th><th className="pf-l">Type</th><th className="pf-l">G / D</th><th title="Percent of NAV, signed (5 = buy 5% of NAV, -3 = sell 3%)">Trade % NAV</th><th className="pf-l">Ccy</th><th>Mult</th><th>Delta</th><th>Price</th><th className="pf-l">Px date</th><th>FX</th><th>Loc val</th><th>Phys wt</th><th>A$ value</th><th>Notional wt</th><th>Qty Fund</th><th>Qty SN</th><th /></tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const c = calc[i];
              return (
                <tr key={i}>
                  <td><input value={r.ticker} style={{ minWidth: 70 }} onChange={(e) => { const sym = e.target.value.toUpperCase(); const a = aplBySym[sym]; update(i, { ticker: sym, ...(a ? { name: a.security_description || r.name, ccy: a.currency || r.ccy, gd: a.growth_defensive || r.gd, asset_type: a.asset_type || r.asset_type, ds_ticker: a.ds_ticker || '' } : {}) }); }} /></td>
                  <td><input value={r.name || ''} style={{ minWidth: 150 }} onChange={(e) => update(i, { name: e.target.value })} /></td>
                  <td><select value={r.asset_type} onChange={(e) => update(i, { asset_type: e.target.value })}>{['STK', 'OPT', 'FUT'].map((o) => <option key={o}>{o}</option>)}</select></td>
                  <td><select value={r.gd} onChange={(e) => update(i, { gd: e.target.value })}>{['Growth', 'Defensive'].map((o) => <option key={o}>{o}</option>)}</select></td>
                  <td><input className="pf-num" type="number" step="0.5" value={r.trade_pct ?? ''} onChange={(e) => update(i, { trade_pct: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                  <td><input value={r.ccy} style={{ width: 50 }} onChange={(e) => update(i, { ccy: e.target.value.toUpperCase() })} /></td>
                  <td><input className="pf-num" type="number" value={r.multiplier ?? 1} onChange={(e) => update(i, { multiplier: Number(e.target.value) })} /></td>
                  <td><input className="pf-num" type="number" step="0.01" value={r.delta ?? 1} onChange={(e) => update(i, { delta: Number(e.target.value) })} /></td>
                  <td><input className="pf-num" type="number" step="0.01" value={r.price ?? ''} onChange={(e) => update(i, { price: e.target.value === '' ? null : Number(e.target.value) })} title={r.price_reason || ''} /></td>
                  <td className="pf-l pf-sub" title={r.price_reason || ''}>{r.price_date || (r.price_reason ? 'no series: type price' : '')}</td>
                  <td><input className="pf-num" type="number" step="0.0001" value={r.ccy === 'AUD' ? 1 : (r.fx ?? '')} disabled={r.ccy === 'AUD'} placeholder="FX" title={r.ccy !== 'AUD' && !isNum(r.fx) ? `${r.ccy}AUD not loaded: type the rate` : ''} onChange={(e) => update(i, { fx: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                  <td className="pf-r"><V v={c.loc_val} kind="num" /></td><td className="pf-r"><V v={c.phys_weight} dp={2} signed /></td>
                  <td className="pf-r"><V v={c.aud_val} kind="num" /></td><td className="pf-r"><V v={c.notional} dp={2} signed /></td>
                  <td className="pf-r"><strong><V v={c.qty} kind="num" /></strong></td><td className="pf-r"><V v={c.qty_sn} kind="num" /></td>
                  <td><button className="pf-link" onClick={() => { setRows(rows.filter((_, j) => j !== i)); setDirty(true); }}>remove</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="pf-section"><h2>Portfolio limits</h2></div>
      <div className="pf-table-scroll" style={{ maxWidth: 620 }}>
        <table className="pf-table">
          <thead><tr><th className="pf-l">Limit</th><th>Now</th><th>Post trade</th><th>Limit</th><th className="pf-l">Now</th><th className="pf-l">Post trade</th></tr></thead>
          <tbody>
            {checks.map((k) => (
              <tr key={k.label}>
                <td className="pf-l"><strong>{k.label}</strong></td><td className="pf-r"><V v={k.pre} dp={1} /></td><td className="pf-r"><V v={k.post} dp={1} /></td><td className="pf-r">{pct(k.limit, 0)}</td>
                <td className="pf-l"><span className={`pf-pill ${isNum(k.pre) ? (k.pre < k.limit ? 'pf-pill-ok' : 'pf-pill-breach') : 'pf-pill-navy'}`}>{isNum(k.pre) ? (k.pre < k.limit ? 'OK' : 'Breach') : '–'}</span></td>
                <td className="pf-l"><span className={`pf-pill ${isNum(k.post) ? (k.post < k.limit ? 'pf-pill-ok' : 'pf-pill-breach') : 'pf-pill-navy'}`}>{isNum(k.post) ? (k.post < k.limit ? 'OK' : 'Breach') : '–'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- Applications and redemptions --------------------------------------------------
export function FlowCalcTab({ summary, trailing }) {
  const [flow, setFlow] = useState(0);
  const prices = useMemo(() => { const m = {}; (trailing?.rows || []).forEach((r) => { m[r.security_id] = r; }); return m; }, [trailing]);
  if (!summary) return null;
  const secs = summary.groups.flatMap((g) => g.securities.filter((s) => s.AssetType !== 'CASH').map((s) => ({ ...s, group: g.asset_class })));
  const cash = summary.groups.flatMap((g) => g.securities.filter((s) => s.AssetType === 'CASH'));
  const nav = summary.stats.NAV_Fund;
  const fum = nav + flow;
  const secRows = secs.map((s) => {
    const post = s.Value_Fund; const postW = post / fum; const drift = postW - s.Weight_Fund; const trade = -drift * fum;
    const px = prices[s.SecurityID]?.price; const fx = s.FxRateToBase || 1;
    const qty = isNum(px) && px > 0 ? trade / (px * fx) : null;
    return { ...s, post, postW, drift, trade, px, qty, qty_round: isNum(qty) ? Math.round(qty / 1000) * 1000 : null, check: (post + trade) / fum };
  });
  const cashRows = cash.map((c) => {
    const post = c.Value_Fund + (c.Currency === 'AUD' ? flow : 0); const postW = post / fum; const drift = postW - c.Weight_Fund;
    return { ...c, post, postW, drift, trade: -drift * fum };
  });
  const audShare = cashRows.filter((c) => c.Currency === 'AUD').reduce((a, c) => a + c.postW, 0) + secRows.filter((s) => s.Currency === 'AUD').reduce((a, s) => a + s.postW, 0);
  return (
    <>
      <div className="pf-note">Replaces the Apps and Redemptions sheet. Enter the net cash flow (applications positive, redemptions negative). Post-flow weights assume the flow lands in AUD cash; required trade = −drift x post-flow FUM, units from the latest Datastream close. Units to trade is that number of units rounded to the nearest 1,000, as the sheet did (17,000 means 17,000 units, not thousands).</div>
      <div className="pf-add-row" style={{ maxWidth: 640 }}>
        <label>Net cash flow A$<input type="number" step="100000" value={flow} onChange={(e) => setFlow(Number(e.target.value) || 0)} /></label>
        <Tile label="IB NAV" value={money(nav)} />
        <Tile label="Post-flow FUM" value={money(fum)} />
        <Tile label="Non-AUD post flow" value={pct(1 - audShare, 1)} />
      </div>
      <div className="pf-table-scroll">
        <table className="pf-table">
          <thead><tr><th className="pf-l">Symbol</th><th className="pf-l">Security</th><th className="pf-l">Class</th><th>Value</th><th>Weight</th><th>Post-flow wt</th><th>Drift</th><th>Required trade A$</th><th>Price</th><th>Units</th><th>Units to trade</th><th>Post-trade wt</th></tr></thead>
          <tbody>
            {secRows.map((s) => (
              <tr key={s.SecurityID}>
                <td className="pf-l"><strong>{s.Symbol}</strong></td><td className="pf-l">{s.SecurityDescription}</td><td className="pf-l pf-sub">{s.group}</td>
                <td className="pf-r"><V v={s.post} kind="num" /></td><td className="pf-r"><V v={s.Weight_Fund} dp={2} /></td><td className="pf-r"><V v={s.postW} dp={2} /></td>
                <td className={`pf-r ${signCls(s.drift)}`}><V v={s.drift} dp={2} signed /></td><td className={`pf-r ${signCls(s.trade)}`}><V v={s.trade} kind="num" /></td>
                <td className="pf-r"><V v={s.px} kind="num" dp={2} /></td><td className="pf-r"><V v={s.qty} kind="num" dp={0} /></td><td className="pf-r"><strong><V v={s.qty_round} kind="num" dp={0} /></strong></td>
                <td className="pf-r"><V v={s.check} dp={2} /></td>
              </tr>
            ))}
            {cashRows.map((c, i) => (
              <tr key={'c' + i} style={{ background: '#fafafa' }}>
                <td className="pf-l"><strong>Cash {c.Currency}</strong></td><td className="pf-l" colSpan={2} />
                <td className="pf-r"><V v={c.post} kind="num" /></td><td className="pf-r"><V v={c.Weight_Fund} dp={2} /></td><td className="pf-r"><V v={c.postW} dp={2} /></td>
                <td className={`pf-r ${signCls(c.drift)}`}><V v={c.drift} dp={2} signed /></td><td className={`pf-r ${signCls(c.trade)}`}><V v={c.trade} kind="num" /></td><td colSpan={4} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---- History tab -------------------------------------------------------------------
export function HistoryTab() {
  const h = useFetch('/api/plus-fund/history');
  const d = h.data;
  const nav = useMemo(() => (d?.nav || []).map((r) => ({ date: r.date, Fund: r.fund, SN: r.sn, DK: r.dk })), [d]);
  const weights = useMemo(() => (d?.weights || []).map((r) => { const o = { date: r.date }; d.asset_classes.forEach((c) => { o[c] = isNum(r[c]) ? r[c] * 100 : 0; }); return o; }), [d]);
  const monthly = useMemo(() => { // month-end asset-class split from the daily weights
    const by = {}; (d?.weights || []).forEach((r) => { by[r.date.slice(0, 7)] = r; });
    return Object.entries(by).map(([m, r]) => { const o = { date: m }; d.asset_classes.forEach((c) => { o[c] = isNum(r[c]) ? r[c] * 100 : 0; }); return o; });
  }, [d]);
  const expo = useMemo(() => (d?.exposure || []).map((r) => ({ ...r, date: r.date.slice(0, 7) })), [d]);
  return (
    <>
      <Status loading={h.loading} error={h.error} />
      {d && (
        <>
          <div className="pf-grid-2">
            <div className="pf-chart"><h3>NAV through time (IB, A$)</h3>
              <ResponsiveContainer width="100%" height={300}><LineChart data={nav} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={50} /><YAxis tickFormatter={mTick} tick={{ fontSize: 11 }} width={60} />
                <Tooltip formatter={(v) => money(v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="Fund" stroke={C.navy} dot={false} strokeWidth={2} /><Line type="monotone" dataKey="SN" stroke={C.teal} dot={false} /><Line type="monotone" dataKey="DK" stroke={C.lightBlue} dot={false} />
              </LineChart></ResponsiveContainer></div>
            <div className="pf-chart"><h3>Exposure split at month end (share of NAV)</h3>
              <ResponsiveContainer width="100%" height={300}><LineChart data={expo} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} /><YAxis tickFormatter={pctTick} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={pctTip} /><Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="gross" name="Gross" stroke={C.navy} dot={false} strokeWidth={2} /><Line type="monotone" dataKey="growth_net" name="Growth" stroke={C.tealDark} dot={false} />
                <Line type="monotone" dataKey="defensive_net" name="Defensive" stroke={C.slate} dot={false} /><Line type="monotone" dataKey="fx" name="FX (non-AUD)" stroke={C.mauve} dot={false} />
              </LineChart></ResponsiveContainer></div>
          </div>
          <div className="pf-grid-2" style={{ marginTop: 16 }}>
            <div className="pf-chart"><h3>Daily notional weight by asset class (% of NAV)</h3>
              <ResponsiveContainer width="100%" height={320}><AreaChart data={weights} margin={{ left: 0, right: 20, top: 5, bottom: 5 }} stackOffset="sign">
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={50} /><YAxis tickFormatter={(v) => v.toFixed(0) + '%'} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={(v) => v.toFixed(1) + '%'} /><Legend wrapperStyle={{ fontSize: 11 }} /><ReferenceLine y={0} stroke={C.navy} />
                {d.asset_classes.map((c, i) => <Area key={c} type="monotone" dataKey={c} stackId="a" stroke={AC_COLOURS[i % AC_COLOURS.length]} fill={AC_COLOURS[i % AC_COLOURS.length]} fillOpacity={0.85} />)}
              </AreaChart></ResponsiveContainer></div>
            <div className="pf-chart"><h3>Month-end asset class split (% of NAV)</h3>
              <ResponsiveContainer width="100%" height={320}><BarChart data={monthly} margin={{ left: 0, right: 20, top: 5, bottom: 5 }} stackOffset="sign">
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} /><YAxis tickFormatter={(v) => v.toFixed(0) + '%'} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={(v) => v.toFixed(1) + '%'} /><Legend wrapperStyle={{ fontSize: 11 }} /><ReferenceLine y={0} stroke={C.navy} />
                {d.asset_classes.map((c, i) => <Bar key={c} dataKey={c} stackId="a" fill={AC_COLOURS[i % AC_COLOURS.length]} />)}
              </BarChart></ResponsiveContainer></div>
          </div>
          <div className="pf-foot">From the daily IB holdings history (Jan 2021 onward). Exposure split recomputed per day with the Download_IB_Data.py rules: gross = Σ|notional| ex cash / NAV, growth and defensive = signed notional of each bucket / NAV (defensive ex cash), FX = weight of non-AUD exposure.</div>
        </>
      )}
    </>
  );
}

// ---- Contribution tab ------------------------------------------------------------------
export function ContributionTab() {
  const c = useFetch('/api/plus-fund/contributions');
  const [window, setWindow] = useState(24);
  const d = c.data;
  const monthly = useMemo(() => (d?.monthly || []).slice(-window).map((r) => { const o = { month: r.month }; d.groups.forEach((g) => { o[g] = r[g] * 100; }); o.Fund = isNum(r.fund_return) ? r.fund_return * 100 : null; return o; }), [d, window]);
  const cum = useMemo(() => (d?.cumulative || []).map((r) => { const o = { month: r.month }; d.groups.forEach((g) => { o[g] = r[g]; }); return o; }), [d]);
  return (
    <>
      <div className="pf-note">Monthly return contribution by asset group from the IB P/L and Activity files: position and transaction mark-to-market, commissions and GST, dividends and withholding tax, divided by the month's average daily NAV. FX = cash translation gain/loss. Feb 2021 to Nov 2022 are the old workbook's hand-built figures (source "sheet"). Groups follow the APL asset class: Aus / Global Equities, Alternatives (infrastructure, property, commodities, global alpha), Credit, Bonds, Portfolio Protection (hedges), FX, Cash & interest.</div>
      <Status loading={c.loading} error={c.error} />
      {d && (
        <>
          <div className="pf-toolbar"><label className="pf-sub">Window <select value={window} onChange={(e) => setWindow(Number(e.target.value))}>{[12, 24, 36, 60, 120].map((w) => <option key={w} value={w}>{w} months</option>)}</select></label></div>
          <div className="pf-grid-2">
            <div className="pf-chart"><h3>Monthly contribution by asset group (pp)</h3>
              <ResponsiveContainer width="100%" height={340}><BarChart data={monthly} margin={{ left: 0, right: 20, top: 5, bottom: 5 }} stackOffset="sign">
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11 }} minTickGap={30} /><YAxis tickFormatter={(v) => v.toFixed(1)} tick={{ fontSize: 11 }} width={45} />
                <Tooltip formatter={(v) => (isNum(v) ? v.toFixed(2) + ' pp' : '–')} /><Legend wrapperStyle={{ fontSize: 11 }} /><ReferenceLine y={0} stroke={C.navy} />
                {d.groups.map((g) => <Bar key={g} dataKey={g} stackId="a" fill={GROUP_COLOURS[g] || C.greyMid} />)}
                <Line type="monotone" dataKey="Fund" name="Fund return" stroke={C.danger} dot={{ r: 2 }} strokeWidth={1.5} />
              </BarChart></ResponsiveContainer></div>
            <div className="pf-chart"><h3>Cumulative contribution by asset group (sum of monthly)</h3>
              <ResponsiveContainer width="100%" height={340}><LineChart data={cum} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11 }} minTickGap={40} /><YAxis tickFormatter={pctTick} tick={{ fontSize: 11 }} width={50} />
                <Tooltip formatter={pctTip} /><Legend wrapperStyle={{ fontSize: 11 }} /><ReferenceLine y={0} stroke={C.navy} />
                {d.groups.map((g) => <Line key={g} type="monotone" dataKey={g} stroke={GROUP_COLOURS[g] || C.greyMid} dot={false} strokeWidth={g === 'Cash & interest' ? 1 : 1.8} />)}
              </LineChart></ResponsiveContainer></div>
          </div>
          <div className="pf-section"><h2>Last 12 months</h2></div>
          <div className="pf-table-scroll">
            <table className="pf-table">
              <thead><tr><th className="pf-l">Month</th>{d.groups.map((g) => <th key={g}>{g}</th>)}<th>Total</th><th>Fund return</th><th>Avg NAV</th><th className="pf-l">Source</th></tr></thead>
              <tbody>
                {d.monthly.slice(-12).reverse().map((r) => (
                  <tr key={r.month}><td className="pf-l"><strong>{r.month}</strong></td>
                    {d.groups.map((g) => <td key={g} className={`pf-r ${signCls(r[g])}`}><V v={r[g]} dp={2} signed /></td>)}
                    <td className={`pf-r ${signCls(r.total)}`}><strong><V v={r.total} dp={2} signed /></strong></td>
                    <td className={`pf-r ${signCls(r.fund_return)}`}><V v={r.fund_return} dp={2} signed /></td>
                    <td className="pf-r"><V v={r.nav} kind="money" /></td><td className="pf-l pf-sub">{r.source}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pf-foot">Total differs from the fund return by fees and by the APEX unit-price timing versus IB's daily marks.</div>
        </>
      )}
    </>
  );
}

// ---- Flows tab ------------------------------------------------------------------------
export function FlowsTab() {
  const f = useFetch('/api/plus-fund/flows');
  const d = f.data;
  const weekly = useMemo(() => (d?.weekly || []).map((r) => ({ date: r.date, amount: r.amount })), [d]);
  const total = (d?.weekly || []).reduce((a, r) => a + (r.amount || 0), 0);
  return (
    <>
      <Status loading={f.loading} error={f.error} />
      {d && (
        <>
          <div className="pf-tiles"><Tile label="Deposits since Jan 2023" value={money(total)} /><Tile label="Last week" value={money(weekly[weekly.length - 1]?.amount)} sub={weekly[weekly.length - 1]?.date} /></div>
          <div className="pf-chart"><h3>Weekly deposit flows (IB cash report, A$)</h3>
            <ResponsiveContainer width="100%" height={300}><BarChart data={weekly} margin={{ left: 0, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid stroke="#eef1f4" vertical={false} /><XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={50} /><YAxis tickFormatter={mTick} tick={{ fontSize: 11 }} width={60} />
              <Tooltip formatter={(v) => money(v)} /><Bar dataKey="amount" name="Deposits" fill={C.teal} />
            </BarChart></ResponsiveContainer></div>
          <div className="pf-section"><h2>Deposit and withdrawal events</h2></div>
          <div className="pf-table-scroll" style={{ maxWidth: 560 }}>
            <table className="pf-table"><thead><tr><th className="pf-l">Date</th><th className="pf-l">Type</th><th>Amount A$</th></tr></thead>
              <tbody>{[...d.events].reverse().slice(0, 60).map((e, i) => <tr key={i}><td className="pf-l">{e.date}</td><td className="pf-l">{e.label}</td><td className={`pf-r ${signCls(e.amount)}`}><V v={e.amount} kind="num" /></td></tr>)}</tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
